import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Users,
  Coffee,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  RefreshCw,
  Edit3,
  Check,
  X,
  Plus,
  Sliders,
  History,
  ShieldAlert,
  Download,
  Settings,
  FileSpreadsheet,
  CheckCheck,
  ChevronRight,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import {
  getManagerOverview,
  reconcileAttendance,
  reviewOvertime,
  listShifts,
  createShift,
  updateShift,
  assignUserShift,
  getWorkforce,
  exportWorkforce,
  getPolicies,
  setOutletPolicy,
  setRolePolicy,
  setUserOverrides,
} from "@/lib/attendanceApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/lang";


function toLocalDatetimeInput(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatMinutes(mins) {
  if (!mins || mins <= 0) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function PolicyConfigModal({ open, onClose, token, onPolicyUpdated }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("outlet");
  const [outletForm, setOutletForm] = useState({
    normal_minutes: 600,
    half_day_max_minutes: 360,
    grace_period_minutes: 15,
    expected_break_minutes: 45,
    breaks_paid: false,
    overtime_allowed: true,
    min_overtime_minutes: 15,
  });
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleForm, setRoleForm] = useState({});

  const loadPolicies = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getPolicies(token);
      setData(res);
      if (res.outlet) {
        setOutletForm({
          normal_minutes: res.outlet.normal_minutes?.value ?? 600,
          half_day_max_minutes: res.outlet.half_day_max_minutes?.value ?? 360,
          grace_period_minutes: res.outlet.grace_period_minutes?.value ?? 15,
          expected_break_minutes: res.outlet.expected_break_minutes?.value ?? 45,
          breaks_paid: Boolean(res.outlet.breaks_paid?.value),
          overtime_allowed: Boolean(res.outlet.overtime_allowed?.value),
          min_overtime_minutes: res.outlet.min_overtime_minutes?.value ?? 15,
        });
      }
      if (res.roles?.length && !selectedRoleId) {
        setSelectedRoleId(String(res.roles[0].id));
        setRoleForm(res.roles[0].policy || {});
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load policies");
    } finally {
      setLoading(false);
    }
  }, [token, selectedRoleId]);

  useEffect(() => {
    if (open) loadPolicies();
  }, [open, loadPolicies]);

  const handleSaveOutlet = async () => {
    setSaving(true);
    try {
      await setOutletPolicy(token, {
        normal_minutes: Number(outletForm.normal_minutes),
        half_day_max_minutes: Number(outletForm.half_day_max_minutes),
        grace_period_minutes: Number(outletForm.grace_period_minutes),
        expected_break_minutes: Number(outletForm.expected_break_minutes),
        breaks_paid: Boolean(outletForm.breaks_paid),
        overtime_allowed: Boolean(outletForm.overtime_allowed),
        min_overtime_minutes: Number(outletForm.min_overtime_minutes),
      });
      toast.success("Outlet attendance policy updated");
      if (onPolicyUpdated) onPolicyUpdated();
      loadPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update outlet policy");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await setRolePolicy(token, Number(selectedRoleId), roleForm);
      toast.success("Role policy updated");
      if (onPolicyUpdated) onPolicyUpdated();
      loadPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update role policy");
    } finally {
      setSaving(false);
    }
  };

  const handleReviewUser = async (userId) => {
    try {
      await setUserOverrides(token, userId, {}, true);
      toast.success("Staff policy review acknowledged");
      if (onPolicyUpdated) onPolicyUpdated();
      loadPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to acknowledge review");
    }
  };

  if (!open) return null;

  const reviewNeeded = (data?.people || []).filter(p => p.needs_review);
  const pickedRole = (data?.roles || []).find(r => String(r.id) === selectedRoleId);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4">
      <div className="bg-card border rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-3.5 bg-muted/30">
          <div>
            <h3 className="text-base font-bold text-foreground flex items-center gap-2">
              <Sliders className="w-4 h-4 text-primary" />
              Attendance Policies & Working Hours
            </h3>
            <p className="text-xs text-muted-foreground">
              Working hour thresholds, half-day / full-day classification & role overrides
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b px-5 bg-card gap-4 text-xs font-semibold">
          <button
            onClick={() => setActiveSubTab("outlet")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeSubTab === "outlet" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
          >
            Outlet Defaults
          </button>
          <button
            onClick={() => setActiveSubTab("roles")}
            className={`py-2.5 border-b-2 transition-colors ${
              activeSubTab === "roles" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
          >
            Role Overrides ({data?.roles?.length || 0})
          </button>
          <button
            onClick={() => setActiveSubTab("review")}
            className={`py-2.5 border-b-2 transition-colors flex items-center gap-1.5 ${
              activeSubTab === "review" ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
          >
            Review Queue
            {reviewNeeded.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-warning/20 text-warning font-bold">
                {reviewNeeded.length}
              </span>
            )}
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-4 text-xs">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading policies…</div>
          ) : activeSubTab === "outlet" ? (
            <div className="space-y-4">
              <div className="p-3 bg-muted/40 rounded-lg border text-muted-foreground">
                <p>
                  These baseline thresholds apply to all staff unless customized for their role or profile.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1 font-medium">
                  Normal Working Time (minutes)
                  <span className="text-[11px] text-muted-foreground">Default: 600 min (10 hours)</span>
                  <Input
                    type="number"
                    value={outletForm.normal_minutes}
                    onChange={e => setOutletForm(f => ({ ...f, normal_minutes: e.target.value }))}
                    className="h-8"
                  />
                </label>

                <label className="flex flex-col gap-1 font-medium">
                  Half-Day Max Working Time (minutes)
                  <span className="text-[11px] text-muted-foreground">Net time &le; threshold is Half Day (default: 360 min / 6 hours)</span>
                  <Input
                    type="number"
                    value={outletForm.half_day_max_minutes}
                    onChange={e => setOutletForm(f => ({ ...f, half_day_max_minutes: e.target.value }))}
                    className="h-8"
                  />
                </label>

                <label className="flex flex-col gap-1 font-medium">
                  Grace Period (minutes)
                  <span className="text-[11px] text-muted-foreground">Late threshold past shift start</span>
                  <Input
                    type="number"
                    value={outletForm.grace_period_minutes}
                    onChange={e => setOutletForm(f => ({ ...f, grace_period_minutes: e.target.value }))}
                    className="h-8"
                  />
                </label>

                <label className="flex flex-col gap-1 font-medium">
                  Expected Break (minutes)
                  <span className="text-[11px] text-muted-foreground">Target break allotment per shift</span>
                  <Input
                    type="number"
                    value={outletForm.expected_break_minutes}
                    onChange={e => setOutletForm(f => ({ ...f, expected_break_minutes: e.target.value }))}
                    className="h-8"
                  />
                </label>

                <label className="flex flex-col gap-1 font-medium">
                  Min Overtime Threshold (minutes)
                  <span className="text-[11px] text-muted-foreground">Min extra time to qualify as overtime</span>
                  <Input
                    type="number"
                    value={outletForm.min_overtime_minutes}
                    onChange={e => setOutletForm(f => ({ ...f, min_overtime_minutes: e.target.value }))}
                    className="h-8"
                  />
                </label>

                <div className="flex flex-col justify-end gap-2 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={outletForm.overtime_allowed}
                      onChange={e => setOutletForm(f => ({ ...f, overtime_allowed: e.target.checked }))}
                      className="w-4 h-4 rounded text-primary"
                    />
                    <span className="font-medium">Overtime Allowed by Default</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={outletForm.breaks_paid}
                      onChange={e => setOutletForm(f => ({ ...f, breaks_paid: e.target.checked }))}
                      className="w-4 h-4 rounded text-primary"
                    />
                    <span className="font-medium">Breaks are Paid</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end pt-3 border-t">
                <Button onClick={handleSaveOutlet} disabled={saving} size="sm" variant="action">
                  {saving ? "Saving…" : "Save Outlet Defaults"}
                </Button>
              </div>
            </div>
          ) : activeSubTab === "roles" ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-muted-foreground">Select Role:</span>
                <select
                  value={selectedRoleId}
                  onChange={e => {
                    const rId = e.target.value;
                    setSelectedRoleId(rId);
                    const r = (data?.roles || []).find(x => String(x.id) === rId);
                    setRoleForm(r?.policy || {});
                  }}
                  className="h-8 px-2.5 rounded-md border bg-background text-xs font-medium"
                >
                  {(data?.roles || []).map(r => (
                    <option key={r.id} value={r.id}>{r.name} ({r.base_kind})</option>
                  ))}
                </select>
              </div>

              {pickedRole && (
                <div className="space-y-3 p-3 bg-muted/20 border rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    Role-level policy overrides outlet defaults. Blank fields inherit from the outlet.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1">
                      Normal Minutes (blank = inherit)
                      <Input
                        type="number"
                        placeholder={`Inherit (${outletForm.normal_minutes})`}
                        value={roleForm.normal_minutes ?? ""}
                        onChange={e => setRoleForm(rf => ({ ...rf, normal_minutes: e.target.value ? Number(e.target.value) : null }))}
                        className="h-8"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      Half-Day Max Minutes (blank = inherit)
                      <Input
                        type="number"
                        placeholder={`Inherit (${outletForm.half_day_max_minutes})`}
                        value={roleForm.half_day_max_minutes ?? ""}
                        onChange={e => setRoleForm(rf => ({ ...rf, half_day_max_minutes: e.target.value ? Number(e.target.value) : null }))}
                        className="h-8"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      Grace Minutes (blank = inherit)
                      <Input
                        type="number"
                        placeholder={`Inherit (${outletForm.grace_period_minutes})`}
                        value={roleForm.grace_period_minutes ?? ""}
                        onChange={e => setRoleForm(rf => ({ ...rf, grace_period_minutes: e.target.value ? Number(e.target.value) : null }))}
                        className="h-8"
                      />
                    </label>

                    <label className="flex flex-col gap-1">
                      Expected Break (blank = inherit)
                      <Input
                        type="number"
                        placeholder={`Inherit (${outletForm.expected_break_minutes})`}
                        value={roleForm.expected_break_minutes ?? ""}
                        onChange={e => setRoleForm(rf => ({ ...rf, expected_break_minutes: e.target.value ? Number(e.target.value) : null }))}
                        className="h-8"
                      />
                    </label>
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSaveRole} disabled={saving} size="sm" variant="action">
                      {saving ? "Saving…" : `Save ${pickedRole.name} Policy`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Staff members whose role or shift was modified and may need policy acknowledgment:
              </p>
              {reviewNeeded.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground border rounded-lg bg-card">
                  No staff members currently need policy review.
                </div>
              ) : (
                <div className="space-y-2">
                  {reviewNeeded.map(p => (
                    <div key={p.user_id} className="p-3 bg-card border rounded-lg flex items-center justify-between gap-3">
                      <div>
                        <strong className="text-foreground">{p.name}</strong> ({p.employee_id})
                        <p className="text-muted-foreground">{p.staff_role || p.role} · {p.review_reason || "Role or shift modified"}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReviewUser(p.user_id)}
                      >
                        Acknowledge & Confirm
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t px-5 py-3 bg-muted/20 flex justify-end">
          <Button onClick={onClose} variant="outline" size="sm">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function renderDayStatusBadge(cell) {
  if (!cell || cell.status === "none") return <span className="text-muted-foreground/40 text-[10px]">—</span>;
  if (cell.status === "off") return <span className="text-[10px] px-1 py-0.5 rounded text-muted-foreground/60 bg-muted/40 font-medium">Off</span>;
  if (cell.status === "absent") return <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-destructive bg-destructive/10 border border-destructive/20">Absent</span>;

  let bg = "bg-muted text-foreground border-border";
  let label = cell.status;
  if (cell.status === "full_day") {
    bg = "bg-success/15 text-success border-success/30";
    label = "Full";
  } else if (cell.status === "half_day") {
    bg = "bg-warning/15 text-warning border-warning/30";
    label = "Half";
  } else if (cell.status === "on_duty") {
    bg = "bg-primary/15 text-primary border-primary/30";
    label = "Live";
  } else if (cell.status === "needs_review") {
    bg = "bg-destructive/15 text-destructive border-destructive/30";
    label = "Review";
  } else if (cell.status === "exception") {
    bg = "bg-purple-500/15 text-purple-600 dark:text-purple-400 border-purple-500/30";
    label = "Excp";
  }

  const hours = cell.net_minutes > 0 ? `${(cell.net_minutes / 60).toFixed(1)}h` : null;

  return (
    <div
      className={`inline-flex flex-col items-center justify-center px-1.5 py-0.5 rounded border text-[10px] leading-tight font-semibold ${bg}`}
      title={`Status: ${cell.status}${hours ? ` · Net: ${hours}` : ""}${cell.break_minutes ? ` · Break: ${cell.break_minutes}m` : ""}${cell.late_minutes ? ` · Late: ${cell.late_minutes}m` : ""}${cell.ot_pending_minutes ? ` · Pending OT: ${cell.ot_pending_minutes}m` : ""}`}
    >
      <span>{label}</span>
      {hours && <span className="text-[9px] opacity-80">{hours}</span>}
    </div>
  );
}

export default function StaffAttendancePage() {
  const { token, user } = usePOS();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [activeTab, setActiveTab] = useState("presence"); // presence | workforce | unclosed | overtime | shifts | audit
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split("T")[0]);

  // Workforce state
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
  const sevenDaysAgoStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split("T")[0];
  }, []);

  const [wfFromDate, setWfFromDate] = useState(sevenDaysAgoStr);
  const [wfToDate, setWfToDate] = useState(todayStr);
  const [wfRole, setWfRole] = useState("");
  const [wfUserId, setWfUserId] = useState("");
  const [wfLoading, setWfLoading] = useState(false);
  const [wfData, setWfData] = useState(null);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const loadWorkforce = useCallback(async () => {
    if (!token) return;
    setWfLoading(true);
    try {
      const params = {
        from_date: wfFromDate,
        to_date: wfToDate,
      };
      if (wfRole) params.role = wfRole;
      if (wfUserId) params.user_id = Number(wfUserId);
      const res = await getWorkforce(token, params);
      setWfData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load workforce attendance.");
    } finally {
      setWfLoading(false);
    }
  }, [token, wfFromDate, wfToDate, wfRole, wfUserId]);

  useEffect(() => {
    if (activeTab === "workforce") {
      loadWorkforce();
    }
  }, [activeTab, loadWorkforce]);

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const params = {
        from_date: wfFromDate,
        to_date: wfToDate,
      };
      if (wfRole) params.role = wfRole;
      if (wfUserId) params.user_id = Number(wfUserId);
      await exportWorkforce(token, params);
      toast.success(t("कार्यबल रिपोर्ट डाउनलोड हो गई · Workforce report exported"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed.");
    } finally {
      setExportingCsv(false);
    }
  };

  const setPresetRange = (days) => {
    const end = new Date();
    const start = new Date();
    if (days === 1) {
      setWfFromDate(todayStr);
      setWfToDate(todayStr);
      return;
    }
    if (days === 0) {
      start.setDate(1);
    } else {
      start.setDate(end.getDate() - (days - 1));
    }
    setWfFromDate(start.toISOString().split("T")[0]);
    setWfToDate(end.toISOString().split("T")[0]);
  };

  const wfTotals = useMemo(() => {
    if (!wfData?.employees?.length) {
      return { totalStaff: 0, netMinutes: 0, fullDays: 0, halfDays: 0, absences: 0, lateDays: 0, otPendingMinutes: 0, otApprovedMinutes: 0 };
    }
    const res = {
      totalStaff: wfData.employees.length,
      netMinutes: 0,
      fullDays: 0,
      halfDays: 0,
      absences: 0,
      lateDays: 0,
      otPendingMinutes: 0,
      otApprovedMinutes: 0,
    };
    for (const emp of wfData.employees) {
      const t = emp.totals || {};
      res.netMinutes += t.net_minutes || 0;
      res.fullDays += t.full_days || 0;
      res.halfDays += t.half_days || 0;
      res.absences += t.absences || 0;
      res.lateDays += t.late_days || 0;
      res.otPendingMinutes += t.ot_pending_minutes || 0;
      res.otApprovedMinutes += t.ot_approved_minutes || 0;
    }
    return res;
  }, [wfData]);

  // Modal states
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [reconcileAction, setReconcileAction] = useState("close_shift");
  const [reconcileTime, setReconcileTime] = useState("");
  const [reconcileReason, setReconcileReason] = useState("");

  const [overtimeTarget, setOvertimeTarget] = useState(null);
  const [otDecision, setOtDecision] = useState("approved");
  const [otMinutes, setOtMinutes] = useState(0);
  const [otReason, setOtReason] = useState("");

  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [newShift, setNewShift] = useState({
    name: "",
    start_time: "10:00",
    end_time: "19:00",
    grace_period_minutes: 15,
    expected_break_minutes: 45,
    breaks_paid: false,
    overtime_allowed: true,
    min_overtime_minutes: 15,
  });

  const openCreateShiftModal = () => {
    setEditingShiftId(null);
    setNewShift({
      name: "",
      start_time: "10:00",
      end_time: "19:00",
      grace_period_minutes: 15,
      expected_break_minutes: 45,
      breaks_paid: false,
      overtime_allowed: true,
      min_overtime_minutes: 15,
    });
    setShiftModalOpen(true);
  };

  const openEditShiftModal = (shift) => {
    setEditingShiftId(shift.id);
    setNewShift({
      name: shift.name || "",
      start_time: shift.start_time || "10:00",
      end_time: shift.end_time || "19:00",
      grace_period_minutes: shift.grace_period_minutes ?? 15,
      expected_break_minutes: shift.expected_break_minutes ?? 45,
      breaks_paid: Boolean(shift.breaks_paid),
      overtime_allowed: Boolean(shift.overtime_allowed),
      min_overtime_minutes: shift.min_overtime_minutes ?? 15,
    });
    setShiftModalOpen(true);
  };

  const openReconcileModal = (target, action = "close_shift") => {
    setReconcileTarget(target);
    setReconcileAction(action);
    setReconcileReason("");
    if (action === "correct_duty_in") {
      setReconcileTime(toLocalDatetimeInput(target.duty_in_at || new Date().toISOString()));
    } else if (action === "correct_duty_out") {
      setReconcileTime(toLocalDatetimeInput(target.duty_out_at || new Date().toISOString()));
    } else if (action === "close_shift") {
      setReconcileTime(toLocalDatetimeInput(new Date().toISOString()));
    } else {
      setReconcileTime("");
    }
  };

  const [assignUser, setAssignUser] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState("");

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [ovData, shiftsData] = await Promise.all([
        getManagerOverview(token, dateFilter),
        listShifts(token),
      ]);
      setOverview(ovData);
      setShifts(shiftsData);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load attendance data.");
    } finally {
      setLoading(false);
    }
  }, [token, dateFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle shift reconciliation submission
  const handleReconcileSubmit = async () => {
    if (!reconcileReason.trim()) {
      toast.error(t("कारण आवश्यक है · Reason is required"));
      return;
    }
    const isoTime = reconcileTime ? new Date(reconcileTime).toISOString() : null;
    const payload = {
      attendance_id: reconcileTarget.id,
      action: reconcileAction,
      reason: reconcileReason,
    };
    if (reconcileAction === "correct_duty_in") {
      if (!isoTime) {
        toast.error(t("ड्यूटी-इन समय आवश्यक है · Duty-in time is required"));
        return;
      }
      payload.duty_in_at = isoTime;
    } else if (reconcileAction === "correct_duty_out") {
      if (!isoTime) {
        toast.error(t("ड्यूटी-आउट समय आवश्यक है · Duty-out time is required"));
        return;
      }
      payload.duty_out_at = isoTime;
    } else if (reconcileAction === "close_shift") {
      payload.duty_out_at = isoTime || new Date().toISOString();
    }
    try {
      await reconcileAttendance(token, payload);
      toast.success(t("उपस्थिति संशोधित की गई · Attendance updated"));
      setReconcileTarget(null);
      setReconcileReason("");
      setReconcileTime("");
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconciliation failed.");
    }
  };

  // Handle overtime review submission
  const handleOtSubmit = async () => {
    if (!otReason.trim()) {
      toast.error(t("कारण आवश्यक है · Reason is required"));
      return;
    }
    try {
      await reviewOvertime(token, {
        attendance_id: overtimeTarget.id,
        decision: otDecision,
        approved_minutes: otDecision === "partially_approved" ? parseInt(otMinutes, 10) : null,
        reason: otReason,
      });
      toast.success(t("ओवरटाइम निर्णय सहेजा गया · Overtime reviewed"));
      setOvertimeTarget(null);
      setOtReason("");
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Review failed.");
    }
  };

  // Handle shift create / edit
  const handleSaveShift = async () => {
    if (!newShift.name.trim()) {
      toast.error(t("शिफ्ट का नाम आवश्यक है · Shift name required"));
      return;
    }
    try {
      if (editingShiftId) {
        await updateShift(token, editingShiftId, newShift);
        toast.success(t("शिफ्ट अपडेट की गई · Shift updated"));
      } else {
        await createShift(token, newShift);
        toast.success(t("नई शिफ्ट बनाई गई · Shift created"));
      }
      setShiftModalOpen(false);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save shift.");
    }
  };

  const summary = overview?.summary || {};

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background" data-testid="staff-attendance-page">
      {/* TOP HEADER */}
      <div className="border-b bg-card px-3.5 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 shrink-0">
        <div>
          <h1 className="text-base sm:text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" />
            <span className="truncate">{t("कर्मचारी उपस्थिति एवं शिफ्ट प्रबंधन · Staff Attendance & Shift Management")}</span>
          </h1>
          <p className="text-xs text-muted-foreground">{t("हाज़िरी, ब्रेक एवं ओवर-टाइम · Attendance, Breaks & Overtime")}</p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 self-stretch sm:self-auto justify-between sm:justify-end">
          <div className="flex items-center gap-1.5 sm:gap-2 bg-muted px-2.5 sm:px-3 py-1.5 rounded-lg border text-xs flex-1 sm:flex-initial justify-center">
            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground shrink-0" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-transparent border-0 text-foreground focus:outline-none text-xs"
            />
          </div>
          <Button onClick={loadData} variant="outline" size="sm" className="gap-1.5 h-8 text-xs shrink-0">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>{t("रिफ्रेश · Refresh")}</span>
          </Button>
        </div>
      </div>

      {/* KPI COUNTERS */}
      <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 p-2.5 sm:p-4 shrink-0 bg-muted/20 border-b overflow-x-auto scroll-contain-x scrollbar-none">
        <div
          onClick={() => setActiveTab("presence")}
          className={`cursor-pointer p-2 sm:p-2.5 rounded-lg border transition-all min-w-[95px] sm:min-w-0 shrink-0 ${
            activeTab === "presence" ? "bg-success/10 border-success shadow-sm" : "bg-card hover:bg-muted/50"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("ड्यूटी पर · On Duty")}</span>
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-success">
            {summary.on_duty_count || 0}
          </strong>
        </div>

        <div
          onClick={() => setActiveTab("presence")}
          className={`cursor-pointer p-2 sm:p-2.5 rounded-lg border transition-all min-w-[95px] sm:min-w-0 shrink-0 ${
            activeTab === "presence" ? "bg-warning/10 border-warning shadow-sm" : "bg-card hover:bg-muted/50"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("ब्रेक पर · On Break")}</span>
            <Coffee className="w-3.5 h-3.5 text-warning" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-warning">
            {summary.on_break_count || 0}
          </strong>
        </div>

        <div
          onClick={() => setActiveTab("presence")}
          className="p-2 sm:p-2.5 rounded-lg border bg-card hover:bg-muted/50 transition-all cursor-pointer min-w-[95px] sm:min-w-0 shrink-0"
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("देरी से आगमन · Late")}</span>
            <AlertTriangle className="w-3.5 h-3.5 text-warning" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-warning">
            {summary.late_count || 0}
          </strong>
        </div>

        <div
          onClick={() => setActiveTab("unclosed")}
          className={`cursor-pointer p-2 sm:p-2.5 rounded-lg border transition-all min-w-[95px] sm:min-w-0 shrink-0 ${
            activeTab === "unclosed" ? "bg-danger/10 border-danger shadow-sm" : "bg-card hover:bg-muted/50"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("असमाप्त · Unclosed")}</span>
            <ShieldAlert className="w-3.5 h-3.5 text-danger" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-danger">
            {summary.unclosed_count || 0}
          </strong>
        </div>

        <div
          onClick={() => setActiveTab("overtime")}
          className={`cursor-pointer p-2 sm:p-2.5 rounded-lg border transition-all min-w-[95px] sm:min-w-0 shrink-0 ${
            activeTab === "overtime" ? "bg-purple-500/10 border-purple-500 shadow-sm" : "bg-card hover:bg-muted/50"
          }`}
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("लंबित ओवर-टाइम · OT")}</span>
            <Clock className="w-3.5 h-3.5 text-purple-500" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
            {summary.overtime_pending_count || 0}
          </strong>
        </div>

        <div
          onClick={() => setActiveTab("presence")}
          className="p-2 sm:p-2.5 rounded-lg border bg-card hover:bg-muted/50 transition-all cursor-pointer min-w-[95px] sm:min-w-0 shrink-0"
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("पूर्ण शिफ्ट · Done")}</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <strong className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
            {summary.completed_count || 0}
          </strong>
        </div>
      </div>

      {/* NAVIGATION TABS */}
      <div className="flex border-b px-3 sm:px-6 bg-card shrink-0 gap-4 sm:gap-6 text-xs sm:text-sm overflow-x-auto scroll-contain-x scrollbar-none">
        <button
          onClick={() => setActiveTab("presence")}
          data-testid="tab-presence"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "presence"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("सक्रिय कर्मचारी · Live Staff")} ({summary.on_duty_count + summary.on_break_count || 0})
        </button>

        <button
          onClick={() => setActiveTab("workforce")}
          data-testid="tab-workforce"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "workforce"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("कार्यबल उपस्थिति · Workforce")}
        </button>

        <button
          onClick={() => setActiveTab("unclosed")}
          data-testid="tab-unclosed"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "unclosed"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("समीक्षा लंबित · Shift Review")} ({summary.unclosed_count || 0})
        </button>

        <button
          onClick={() => setActiveTab("overtime")}
          data-testid="tab-overtime"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "overtime"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("ओवर-टाइम अनुमोदन · Overtime")} ({summary.overtime_pending_count || 0})
        </button>

        <button
          onClick={() => setActiveTab("shifts")}
          data-testid="tab-shifts"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "shifts"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("शिफ्ट शेड्यूलिंग · Shift Setup")} ({shifts.length})
        </button>

        <button
          onClick={() => setActiveTab("audit")}
          data-testid="tab-audit"
          className={`py-2.5 sm:py-3 font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${
            activeTab === "audit"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("ऑडिट इतिहास · Corrections Audit")}
        </button>
      </div>

      {/* TAB CONTENT AREA */}
      <div className="p-3 sm:p-6 space-y-4 min-w-0 flex-1 overflow-y-auto">
        {/* WORKFORCE CALENDAR & SUMMARY TAB */}
        {activeTab === "workforce" && (
          <div className="space-y-4" data-testid="workforce-tab-content">
            {/* Filter controls and action strip */}
            <div className="bg-card border rounded-xl p-3 sm:p-4 shadow-xs space-y-3">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                {/* Date presets and pickers */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mr-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Range:
                  </span>
                  <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border text-xs">
                    <button
                      type="button"
                      onClick={() => setPresetRange(1)}
                      className={`px-2 py-1 rounded font-medium transition-all ${
                        wfFromDate === todayStr && wfToDate === todayStr
                          ? "bg-background text-foreground shadow-xs font-bold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetRange(7)}
                      className={`px-2 py-1 rounded font-medium transition-all ${
                        wfFromDate === sevenDaysAgoStr && wfToDate === todayStr
                          ? "bg-background text-foreground shadow-xs font-bold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      7 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetRange(30)}
                      className="px-2 py-1 rounded font-medium transition-all text-muted-foreground hover:text-foreground"
                    >
                      30 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => setPresetRange(0)}
                      className="px-2 py-1 rounded font-medium transition-all text-muted-foreground hover:text-foreground"
                    >
                      This Month
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs">
                    <input
                      type="date"
                      value={wfFromDate}
                      onChange={(e) => setWfFromDate(e.target.value)}
                      className="h-8 px-2 rounded-md border bg-background text-foreground text-xs"
                      aria-label="From date"
                    />
                    <span className="text-muted-foreground">to</span>
                    <input
                      type="date"
                      value={wfToDate}
                      onChange={(e) => setWfToDate(e.target.value)}
                      className="h-8 px-2 rounded-md border bg-background text-foreground text-xs"
                      aria-label="To date"
                    />
                  </div>
                </div>

                {/* Role / User filters & Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={wfRole}
                    onChange={(e) => setWfRole(e.target.value)}
                    className="h-8 px-2 rounded-md border bg-background text-xs font-medium"
                    aria-label="Filter by role"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="cashier">Cashier</option>
                    <option value="captain">Captain</option>
                  </select>

                  <select
                    value={wfUserId}
                    onChange={(e) => setWfUserId(e.target.value)}
                    className="h-8 px-2 rounded-md border bg-background text-xs font-medium"
                    aria-label="Filter by staff member"
                  >
                    <option value="">All Staff</option>
                    {(wfData?.employees || []).map((emp) => (
                      <option key={emp.user_id} value={emp.user_id}>
                        {emp.name} ({emp.employee_id})
                      </option>
                    ))}
                  </select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={exportingCsv || wfLoading}
                    className="h-8 gap-1.5 text-xs"
                    data-testid="export-workforce-csv-btn"
                  >
                    <Download className={`w-3.5 h-3.5 ${exportingCsv ? "animate-bounce" : ""}`} />
                    <span>Export CSV</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPolicyModalOpen(true)}
                    className="h-8 gap-1.5 text-xs text-primary border-primary/30 hover:bg-primary/10"
                    data-testid="open-policy-modal-btn"
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    <span>Policy & Rules</span>
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadWorkforce}
                    disabled={wfLoading}
                    className="h-8 w-8 p-0"
                    title="Refresh workforce"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${wfLoading ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Aggregated KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Active Staff</span>
                <strong className="text-lg font-bold text-foreground">{wfTotals.totalStaff}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Total Net Time</span>
                <strong className="text-lg font-bold text-foreground">{formatMinutes(wfTotals.netMinutes)}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Full Days</span>
                <strong className="text-lg font-bold text-success">{wfTotals.fullDays}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Half Days</span>
                <strong className="text-lg font-bold text-warning">{wfTotals.halfDays}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Absences</span>
                <strong className="text-lg font-bold text-destructive">{wfTotals.absences}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Late Days</span>
                <strong className="text-lg font-bold text-amber-600 dark:text-amber-400">{wfTotals.lateDays}</strong>
              </div>
              <div className="bg-card border rounded-lg p-2.5 shadow-2xs">
                <span className="text-[11px] text-muted-foreground block">Pending OT</span>
                <strong className="text-lg font-bold text-purple-600 dark:text-purple-400">
                  {formatMinutes(wfTotals.otPendingMinutes)}
                </strong>
              </div>
            </div>

            {/* Workforce matrix table */}
            <div className="bg-card border rounded-xl shadow-xs overflow-hidden">
              <div className="overflow-x-auto scroll-contain-x">
                <table className="w-full text-xs border-collapse text-left">
                  <thead>
                    <tr className="bg-muted/50 border-b text-[11px] font-semibold text-muted-foreground">
                      <th className="py-2.5 px-3 sticky left-0 bg-muted/90 backdrop-blur-xs z-10 min-w-[170px] shadow-sm">
                        Staff Member
                      </th>
                      <th className="py-2.5 px-2 min-w-[120px]">Shift / Schedule</th>
                      <th className="py-2.5 px-2 text-right min-w-[70px]">Net Time</th>
                      <th className="py-2.5 px-2 text-center min-w-[75px]">Full / Half</th>
                      <th className="py-2.5 px-2 text-center min-w-[60px]">Absent</th>
                      <th className="py-2.5 px-2 text-center min-w-[60px]">Late</th>
                      <th className="py-2.5 px-2 text-center min-w-[90px]">Overtime</th>
                      {(wfData?.days || []).map((dStr) => {
                        const d = new Date(dStr + "T00:00:00");
                        const dayName = d.toLocaleDateString(undefined, { weekday: "short" });
                        const dayNum = d.getDate();
                        const isSunday = d.getDay() === 0;
                        return (
                          <th
                            key={dStr}
                            className={`py-2 px-1 text-center min-w-[46px] border-l border-border/40 ${
                              isSunday ? "bg-muted/70 text-primary font-bold" : ""
                            }`}
                          >
                            <span className="block text-[10px] uppercase opacity-75">{dayName}</span>
                            <span className="block text-xs font-bold">{dayNum}</span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {wfLoading ? (
                      <tr>
                        <td colSpan={7 + (wfData?.days?.length || 0)} className="py-12 text-center text-muted-foreground">
                          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-primary" />
                          Loading workforce history…
                        </td>
                      </tr>
                    ) : !wfData?.employees?.length ? (
                      <tr>
                        <td colSpan={7 + (wfData?.days?.length || 0)} className="py-12 text-center text-muted-foreground">
                          No staff attendance records found for the selected range.
                        </td>
                      </tr>
                    ) : (
                      wfData.employees.map((emp) => {
                        const t = emp.totals || {};
                        return (
                          <tr key={emp.user_id} className="hover:bg-muted/30 transition-colors">
                            {/* Employee column (sticky) */}
                            <td className="py-2.5 px-3 sticky left-0 bg-card z-10 shadow-sm border-r border-border/40">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-foreground text-xs truncate max-w-[120px]">
                                  {emp.name}
                                </span>
                                {emp.overrides_need_review && (
                                  <span
                                    title="Policy review needed (shift or role changed)"
                                    className="text-amber-500 shrink-0 cursor-pointer"
                                    onClick={() => setPolicyModalOpen(true)}
                                  >
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <span className="font-mono">{emp.employee_id}</span>
                                <span>·</span>
                                <span className="capitalize">{emp.staff_role || emp.role}</span>
                              </div>
                            </td>

                            {/* Shift timing */}
                            <td className="py-2 px-2 text-[11px] text-muted-foreground truncate max-w-[130px]">
                              <p className="font-medium text-foreground truncate">{emp.shift_name || "Default Shift"}</p>
                              {emp.shift_timing && <p className="text-[10px] opacity-80">{emp.shift_timing}</p>}
                            </td>

                            {/* Net Time */}
                            <td className="py-2 px-2 text-right font-mono font-medium text-foreground">
                              {formatMinutes(t.net_minutes)}
                            </td>

                            {/* Full / Half */}
                            <td className="py-2 px-2 text-center">
                              <span className="text-success font-semibold">{t.full_days}</span>
                              <span className="text-muted-foreground mx-0.5">/</span>
                              <span className="text-warning font-semibold">{t.half_days}</span>
                            </td>

                            {/* Absences */}
                            <td className="py-2 px-2 text-center">
                              <span className={t.absences > 0 ? "font-bold text-destructive" : "text-muted-foreground"}>
                                {t.absences}
                              </span>
                            </td>

                            {/* Late */}
                            <td className="py-2 px-2 text-center">
                              <span className={t.late_days > 0 ? "font-semibold text-amber-500" : "text-muted-foreground"}>
                                {t.late_days}
                              </span>
                            </td>

                            {/* Overtime */}
                            <td className="py-2 px-2 text-center whitespace-nowrap">
                              {t.ot_pending_minutes > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setActiveTab("overtime")}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
                                  title="Click to review pending overtime"
                                >
                                  +{formatMinutes(t.ot_pending_minutes)} OT
                                </button>
                              ) : t.ot_approved_minutes > 0 ? (
                                <span className="text-[11px] text-muted-foreground font-mono">
                                  {formatMinutes(t.ot_approved_minutes)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/40 text-[10px]">—</span>
                              )}
                            </td>

                            {/* Daily calendar cells */}
                            {(wfData?.days || []).map((dStr) => {
                              const cell = emp.days?.[dStr];
                              return (
                                <td
                                  key={dStr}
                                  className="py-1 px-1 text-center border-l border-border/30"
                                >
                                  {renderDayStatusBadge(cell)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table Footer Status Legend */}
            <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/20 border rounded-lg text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">Legend:</span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-success inline-block" /> Full Day (&gt;6h)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-warning inline-block" /> Half Day (&le;6h)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" /> Live (On duty)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-destructive inline-block" /> Needs Review / Absent
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block" /> Exception / OT
              </span>
            </div>
          </div>
        )}

        {/* 1. LIVE PRESENCE TAB */}
        {activeTab === "presence" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
                {t("वर्तमान में ड्यूटी / ब्रेक पर (Currently Active)")}
              </h2>
              {overview?.on_duty.length === 0 && overview?.on_break.length === 0 ? (
                <div className="bg-card border rounded-xl p-8 text-center text-muted-foreground">
                  {t("कोई कर्मचारी सक्रिय ड्यूटी पर नहीं है · No staff currently on duty.")}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...(overview?.on_duty || []), ...(overview?.on_break || [])].map((item) => (
                    <div
                      key={item.id}
                      className="bg-card border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-all space-y-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <strong className="text-base text-foreground font-bold">{item.employee_name}</strong>
                          <p className="text-xs text-muted-foreground">
                            {item.employee_id} · <span className="capitalize">{item.role}</span>
                          </p>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                            item.state === "ON_DUTY"
                              ? "bg-success/15 text-success border-success/30"
                              : "bg-warning/15 text-warning border-warning/30"
                          }`}
                        >
                          {item.state === "ON_DUTY" ? t("ड्यूटी पर · On Duty") : t("ब्रेक पर · Break")}
                        </span>
                      </div>

                      <div className="bg-muted/40 p-2.5 rounded-lg space-y-1.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("शिफ्ट · Shift")}:</span>
                          <span className="font-medium text-foreground">{item.shift_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t("कार्य अवधि · Elapsed")}:</span>
                          <span className="font-bold text-foreground">{formatMinutes(item.elapsed_minutes)}</span>
                        </div>
                        {item.total_break_minutes > 0 && (
                          <div className="flex justify-between text-muted-foreground">
                            <span>{t("ब्रेक अवधि · Breaks")}:</span>
                            <span>{formatMinutes(item.total_break_minutes)}</span>
                          </div>
                        )}
                        {item.late_minutes > 0 && (
                          <div className="flex items-center gap-1 text-warning font-semibold pt-1 border-t">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>{t(`${item.late_minutes}m देरी से आगमन · ${item.late_minutes}m late arrival`)}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end gap-1.5 pt-1">
                        <Button
                          onClick={() => openReconcileModal(item, "correct_duty_in")}
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 sm:h-8 px-2.5 gap-1"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          <span>{t("सुधारें · Edit")}</span>
                        </Button>
                        <Button
                          onClick={() => openReconcileModal(item, "close_shift")}
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 sm:h-8 px-2.5 text-danger hover:bg-danger/10 border-danger/30"
                        >
                          {t("ड्यूटी समाप्त करें · Close")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* COMPLETED TODAY */}
            {overview?.completed?.length > 0 && (
              <div className="pt-4">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  {t("आज पूर्ण हुई शिफ्ट (Completed Shifts Today)")}
                </h2>
                <div className="bg-card border rounded-xl overflow-x-auto shadow-sm">
                  <table className="w-full text-left text-xs min-w-[500px]">
                    <thead className="bg-muted/50 border-b font-semibold text-muted-foreground">
                      <tr>
                        <th className="p-3">{t("कर्मचारी · Staff")}</th>
                        <th className="p-3">{t("शिफ्ट · Shift")}</th>
                        <th className="p-3">{t("आगमन · In")}</th>
                        <th className="p-3">{t("प्रस्थान · Out")}</th>
                        <th className="p-3">{t("कार्य समय · Net")}</th>
                        <th className="p-3">{t("ओवरटाइम · OT")}</th>
                        <th className="p-3 text-right">{t("कार्रवाई · Action")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {overview.completed.map((c) => (
                        <tr key={c.id} className="hover:bg-muted/20">
                          <td className="p-3 font-medium">
                            {c.employee_name} ({c.employee_id})
                          </td>
                          <td className="p-3">{c.shift_name}</td>
                          <td className="p-3">{c.duty_in_at ? new Date(c.duty_in_at).toLocaleTimeString() : "—"}</td>
                          <td className="p-3">{c.duty_out_at ? new Date(c.duty_out_at).toLocaleTimeString() : "—"}</td>
                          <td className="p-3 font-semibold">{formatMinutes(c.net_working_minutes)}</td>
                          <td className="p-3">
                            {c.approved_overtime_minutes > 0 ? (
                              <span className="text-success font-bold">
                                +{formatMinutes(c.approved_overtime_minutes)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              onClick={() => openReconcileModal(c, "correct_duty_out")}
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs px-2 gap-1 text-primary hover:bg-primary/10"
                              title={t("शिफ्ट समय सुधारें · Edit shift times")}
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">{t("सुधारें · Edit")}</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2. UNCLOSED & EXCEPTIONS TAB */}
        {activeTab === "unclosed" && (
          <div className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {t("असमाप्त एवं समीक्षा लंबित शिफ्ट (Forgotten Logouts / Pending Review)")}
            </h2>
            {overview?.not_checked_out?.length === 0 ? (
              <div className="bg-card border rounded-xl p-8 text-center text-muted-foreground">
                {t("कोई असमाप्त रिकॉर्ड नहीं है · All shifts have been closed cleanly.")}
              </div>
            ) : (
              <div className="space-y-3">
                {overview.not_checked_out.map((u) => (
                  <div
                    key={u.id}
                    className="bg-card border-l-4 border-l-rose-500 border rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <strong className="text-base text-foreground font-bold">{u.employee_name}</strong>
                        <span className="text-xs text-muted-foreground">({u.employee_id})</span>
                        <span className="bg-danger/10 text-danger border border-danger/30 px-2 py-0.5 rounded-full text-xs font-semibold">
                          {t("समीक्षा आवश्यक · Review")}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("शिफ्ट · Shift")}: {u.shift_name} ({u.shift_timing}) · {t("आगमन · In")}:{" "}
                        {u.duty_in_at ? new Date(u.duty_in_at).toLocaleTimeString() : "—"}
                      </p>
                      <p className="text-xs text-danger font-medium mt-1">
                        {u.exception_reason || t("कर्मचारी ने बिना ड्यूटी-आउट के शिफ्ट छोड़ दी · Employee left without duty-out")}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => {
                          openReconcileModal(u, "close_shift");
                          setReconcileReason(t("मैनेजर द्वारा शिफ्ट पूर्ण की गई · Closed by manager"));
                        }}
                        size="sm"
                        className="h-8 text-xs bg-primary text-primary-foreground font-semibold px-2.5"
                      >
                        <Edit3 className="w-3.5 h-3.5 mr-1" />
                        {t("संशोधित करें एवं बंद करें · Reconcile")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 3. OVERTIME TAB */}
        {activeTab === "overtime" && (
          <div className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {t("स्वीकृति हेतु लंबित ओवर-टाइम (Pending Overtime Approvals)")}
            </h2>
            {overview?.overtime_pending?.length === 0 ? (
              <div className="bg-card border rounded-xl p-8 text-center text-muted-foreground">
                {t("कोई लंबित ओवर-टाइम नहीं है · No pending overtime approval requests.")}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {overview.overtime_pending.map((ot) => (
                  <div key={ot.id} className="bg-card border rounded-xl p-4 shadow-sm space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <strong className="text-base text-foreground font-bold">{ot.employee_name}</strong>
                        <p className="text-xs text-muted-foreground">
                          {ot.employee_id} · {ot.shift_name}
                        </p>
                      </div>
                      <span className="bg-purple-500/15 text-purple-700 dark:text-purple-400 px-3 py-1 rounded-full text-xs font-bold border border-purple-500/30">
                        +{formatMinutes(ot.potential_overtime_minutes)} OT
                      </span>
                    </div>

                    <div className="bg-muted/40 p-3 rounded-lg text-xs space-y-1 border">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("कुल कार्य अवधि · Net Working")}:</span>
                        <span className="font-bold">{formatMinutes(ot.net_working_minutes)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("नियमित शिफ्ट समय · Regular")}:</span>
                        <span>{formatMinutes(ot.regular_working_minutes)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("संभावित ओवर-टाइम · Potential")}:</span>
                        <span className="font-bold text-purple-600">{formatMinutes(ot.potential_overtime_minutes)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t">
                      <Button
                        onClick={() => {
                          setOvertimeTarget(ot);
                          setOtDecision("rejected");
                          setOtReason(t("अतिरिक्त समय अमान्य · Unapproved extra time"));
                        }}
                        variant="ghost"
                        size="sm"
                        className="h-7 sm:h-8 text-xs text-danger hover:bg-danger/10 px-2.5"
                      >
                        {t("अस्वीकार · Reject")}
                      </Button>
                      <Button
                        onClick={() => {
                          setOvertimeTarget(ot);
                          setOtDecision("partially_approved");
                          setOtMinutes(Math.round(ot.potential_overtime_minutes / 2));
                          setOtReason(t("आंशिक अनुमोदन · Partially approved"));
                        }}
                        variant="outline"
                        size="sm"
                        className="h-7 sm:h-8 text-xs px-2.5"
                      >
                        {t("आंशिक · Partial")}
                      </Button>
                      <Button
                        onClick={() => {
                          setOvertimeTarget(ot);
                          setOtDecision("approved");
                          setOtMinutes(ot.potential_overtime_minutes);
                          setOtReason(t("पूर्ण ओवर-टाइम स्वीकृत · Approved full OT"));
                        }}
                        size="sm"
                        className="h-7 sm:h-8 bg-success hover:bg-success text-white text-xs font-bold px-2.5"
                      >
                        {t("पूर्ण स्वीकृति · Approve")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 4. SHIFTS SETUP TAB */}
        {activeTab === "shifts" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t("कॉन्फ़िगर की गई शिफ्ट (Configured Shift Schedules)")}
                </h2>
                <p className="text-xs text-muted-foreground">{t("शिफ्ट समय, मध्यरात्रि सीमा और नियम निर्धारित करें · Define shift timings, midnight boundaries, and rules.")}</p>
              </div>
              <Button onClick={openCreateShiftModal} size="sm" className="h-8 text-xs gap-1.5 font-bold">
                <Plus className="w-3.5 h-3.5" />
                {t("नई शिफ्ट जोड़ें · Add Shift")}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shifts.map((s) => (
                <div key={s.id} className="bg-card border rounded-xl p-4 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <strong className="text-sm sm:text-base text-foreground font-bold">{s.name}</strong>
                      {s.is_default && (
                        <span className="bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 rounded-full border border-primary/20">
                          Default
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditShiftModal(s)}
                      className="h-7 text-xs px-2.5 gap-1"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>{t("संपादित करें · Edit")}</span>
                    </Button>
                  </div>

                  <div className="bg-muted/40 p-3 rounded-lg text-xs space-y-1.5 border">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("समय · Timings")}:</span>
                      <strong className="text-foreground">
                        {s.start_time} - {s.end_time}
                      </strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("अनुमत ग्रेस · Grace")}:</span>
                      <span>{s.grace_period_minutes} min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("अनुपेक्षित ब्रेक · Break")}:</span>
                      <span>{s.expected_break_minutes} min ({s.breaks_paid ? t("सवेतन · Paid") : t("अवैतनिक · Unpaid")})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("ओवर-टाइम · OT")}:</span>
                      <span>{s.overtime_allowed ? t(`अनुमत (न्यूनतम ${s.min_overtime_minutes}m) · Allowed (min ${s.min_overtime_minutes}m)`) : t("अमान्य · Disabled")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 5. AUDIT CORRECTIONS TAB */}
        {activeTab === "audit" && (
          <div className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {t("ऑडिट इतिहास (Audited Manager Corrections Log)")}
            </h2>
            {overview?.completed?.flatMap((c) => c.corrections || []).length === 0 ? (
              <div className="bg-card border rounded-xl p-8 text-center text-muted-foreground">
                {t("कोई संशोधन इतिहास नहीं मिला · No manager corrections logged today.")}
              </div>
            ) : (
              <div className="bg-card border rounded-xl overflow-x-auto shadow-sm">
                <table className="w-full text-left text-xs min-w-[550px]">
                  <thead className="bg-muted/50 border-b font-semibold text-muted-foreground">
                    <tr>
                      <th className="p-3">{t("समय · Time")}</th>
                      <th className="p-3">{t("मैनेजर · Manager")}</th>
                      <th className="p-3">{t("फील्ड · Field")}</th>
                      <th className="p-3">{t("मूल मान · Original")}</th>
                      <th className="p-3">{t("संशोधित मान · Corrected")}</th>
                      <th className="p-3">{t("कारण · Reason")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {overview?.completed
                      ?.flatMap((c) => c.corrections || [])
                      .map((corr) => (
                        <tr key={corr.id} className="hover:bg-muted/20">
                          <td className="p-3 text-muted-foreground">
                            {new Date(corr.created_at).toLocaleTimeString()}
                          </td>
                          <td className="p-3 font-semibold">{corr.corrected_by_name}</td>
                          <td className="p-3">{corr.field_name}</td>
                          <td className="p-3 text-muted-foreground truncate max-w-[150px]">
                            {corr.original_value || "—"}
                          </td>
                          <td className="p-3 font-bold text-success truncate max-w-[150px]">
                            {corr.corrected_value || "—"}
                          </td>
                          <td className="p-3 font-medium">{corr.reason}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RECONCILE MODAL */}
      {reconcileTarget && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 animate-in fade-in duration-150">
          <div className="bg-card border-t sm:border rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 w-full max-w-md shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-0 duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <strong className="text-base font-bold text-foreground">
                {t("शिफ्ट संशोधन · Reconcile Shift")}
              </strong>
              <button onClick={() => setReconcileTarget(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs space-y-1">
              <p>{t("कर्मचारी · Staff")}: <strong>{reconcileTarget.employee_name}</strong> ({reconcileTarget.employee_id})</p>
              <p>{t("शिफ्ट · Shift")}: {reconcileTarget.shift_name}</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">
                  {t("कार्रवाई · Action")}:
                </label>
                <select
                  value={reconcileAction}
                  onChange={(e) => {
                    const nextAction = e.target.value;
                    setReconcileAction(nextAction);
                    if (nextAction === "correct_duty_in") {
                      setReconcileTime(toLocalDatetimeInput(reconcileTarget.duty_in_at));
                    } else if (nextAction === "correct_duty_out") {
                      setReconcileTime(toLocalDatetimeInput(reconcileTarget.duty_out_at || new Date().toISOString()));
                    } else if (nextAction === "close_shift") {
                      setReconcileTime(toLocalDatetimeInput(new Date().toISOString()));
                    }
                  }}
                  className="w-full text-xs p-2 rounded-lg border bg-background text-foreground"
                >
                  <option value="close_shift">{t("शिफ्ट बंद करें · Close Shift")}</option>
                  <option value="correct_duty_out">{t("ड्यूटी-आउट समय सुधारें · Correct Duty-Out Time")}</option>
                  <option value="correct_duty_in">{t("ड्यूटी-इन समय सुधारें · Correct Duty-In Time")}</option>
                  <option value="mark_exception">{t("अपवाद चिह्नित करें · Mark Exception")}</option>
                </select>
              </div>

              {(reconcileAction === "correct_duty_in" ||
                reconcileAction === "correct_duty_out" ||
                reconcileAction === "close_shift") && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">
                    {reconcileAction === "correct_duty_in"
                      ? t("ड्यूटी-इन समय · Duty-In Time")
                      : t("ड्यूटी-आउट समय · Duty-Out Time")}:
                  </label>
                  <Input
                    type="datetime-local"
                    value={reconcileTime}
                    onChange={(e) => setReconcileTime(e.target.value)}
                    className="text-xs h-8 sm:h-9"
                    data-testid="reconcile-time-input"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">
                  {t("अनिवार्य कारण · Mandatory Reason")}:
                </label>
                <Input
                  value={reconcileReason}
                  onChange={(e) => setReconcileReason(e.target.value)}
                  placeholder={t("कारण दर्ज करें (e.g. कर्मचारी साइन-आउट करना भूल गया) · Enter reason (e.g. employee forgot sign-out)")}
                  className="text-xs h-8 sm:h-9"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button onClick={() => setReconcileTarget(null)} variant="ghost" size="sm" className="text-xs">
                {t("रद्द करें · Cancel")}
              </Button>
              <Button onClick={handleReconcileSubmit} size="sm" className="text-xs font-bold">
                {t("सहेजें · Save Correction")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* OVERTIME REVIEW MODAL */}
      {overtimeTarget && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 animate-in fade-in duration-150">
          <div className="bg-card border-t sm:border rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 w-full max-w-md shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-0 duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <strong className="text-base font-bold text-foreground">
                {t("ओवर-टाइम समीक्षा · Overtime Decision")}
              </strong>
              <button onClick={() => setOvertimeTarget(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs space-y-1">
              <p>{t("कर्मचारी · Staff")}: <strong>{overtimeTarget.employee_name}</strong></p>
              <p>{t("संभावित ओवर-टाइम · Potential Overtime")}: <strong className="text-purple-600">{formatMinutes(overtimeTarget.potential_overtime_minutes)}</strong></p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">{t("निर्णय · Decision")}:</label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setOtDecision("approved");
                      setOtMinutes(overtimeTarget.potential_overtime_minutes);
                    }}
                    variant={otDecision === "approved" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    {t("पूर्ण · Full")}
                  </Button>
                  <Button
                    onClick={() => setOtDecision("partially_approved")}
                    variant={otDecision === "partially_approved" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    {t("आंशिक · Partial")}
                  </Button>
                  <Button
                    onClick={() => setOtDecision("rejected")}
                    variant={otDecision === "rejected" ? "destructive" : "outline"}
                    size="sm"
                    className="flex-1 text-xs"
                  >
                    {t("अस्वीकार · Reject")}
                  </Button>
                </div>
              </div>

              {otDecision === "partially_approved" && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">
                    {t("स्वीकृत मिनट · Approved Minutes")}:
                  </label>
                  <Input
                    type="number"
                    value={otMinutes}
                    onChange={(e) => setOtMinutes(e.target.value)}
                    className="text-xs"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">
                  {t("अनिवार्य कारण · Mandatory Reason")}:
                </label>
                <Input
                  value={otReason}
                  onChange={(e) => setOtReason(e.target.value)}
                  placeholder={t("निर्णय का कारण दर्ज करें · Enter decision reason")}
                  className="text-xs"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button onClick={() => setOvertimeTarget(null)} variant="ghost" size="sm" className="text-xs">
                {t("रद्द करें · Cancel")}
              </Button>
              <Button onClick={handleOtSubmit} size="sm" className="text-xs font-bold">
                {t("सहेजें · Save Decision")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE SHIFT MODAL */}
      {shiftModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 animate-in fade-in duration-150">
          <div className="bg-card border-t sm:border rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 w-full max-w-md shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-0 duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <strong className="text-base font-bold text-foreground">
                {editingShiftId ? t("शिफ्ट संपादित करें · Edit Shift") : t("नई शिफ्ट बनाएं · Create Shift")}
              </strong>
              <button onClick={() => setShiftModalOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="font-semibold block mb-1">{t("शिफ्ट का नाम · Shift Name")}:</label>
                <Input
                  value={newShift.name}
                  onChange={(e) => setNewShift({ ...newShift, name: e.target.value })}
                  placeholder="e.g. Night Bar Shift"
                  className="text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold block mb-1">{t("प्रारंभ समय · Start (HH:MM)")}:</label>
                  <Input
                    value={newShift.start_time}
                    onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })}
                    placeholder="18:00"
                    className="text-xs"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1">{t("समाप्ति समय · End (HH:MM)")}:</label>
                  <Input
                    value={newShift.end_time}
                    onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })}
                    placeholder="02:00"
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold block mb-1">{t("ग्रेस अवधि · Grace (mins)")}:</label>
                  <Input
                    type="number"
                    value={newShift.grace_period_minutes}
                    onChange={(e) => setNewShift({ ...newShift, grace_period_minutes: parseInt(e.target.value, 10) })}
                    className="text-xs"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1">{t("ब्रेक अवधि · Break (mins)")}:</label>
                  <Input
                    type="number"
                    value={newShift.expected_break_minutes}
                    onChange={(e) => setNewShift({ ...newShift, expected_break_minutes: parseInt(e.target.value, 10) })}
                    className="text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button onClick={() => setShiftModalOpen(false)} variant="ghost" size="sm" className="text-xs">
                {t("रद्द करें · Cancel")}
              </Button>
              <Button onClick={handleSaveShift} size="sm" className="h-8 text-xs font-bold">
                {editingShiftId ? t("सहेजें · Save Changes") : t("शिफ्ट बनाएं · Create")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* POLICY & RULES CONFIG MODAL */}
      <PolicyConfigModal
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        token={token}
        onPolicyUpdated={loadWorkforce}
      />
    </div>
  );
}
